import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  Chip,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Fade,
  alpha,
  ButtonGroup,
  Tooltip,
  Avatar
} from '@mui/material';
import { 
  ArrowBack, 
  Edit, 
  PlayArrow, 
  Pause, 
  Stop, 
  Store,
  EmojiEvents,
  Assignment,
  AccessTime,
  CheckCircle,
  Campaign as CampaignIcon,
  Insights,
  CardGiftcard,
  Business
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { campaignService } from '../services/campaignService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { Campaign } from '../types';
import { getImageUrl } from '../utils/imageUtils';
import { CampaignImage } from '../components/CampaignImage';
import { useToast } from '../contexts/ToastContext';

const CampaignDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const { showSuccess, showError } = useToast();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCampaign = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await campaignService.getCampaignById(id);
      setCampaign(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar campanha');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DRAFT':
        return 'default';
      case 'COMPLETED':
        return 'info';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Ativa';
      case 'DRAFT':
        return 'Rascunho';
      case 'COMPLETED':
        return 'Concluída';
      case 'CANCELLED':
        return 'Cancelada';
      default:
        return 'Desconhecido';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'ACTIVE':
        return <PlayArrow />;
      case 'DRAFT':
        return <Assignment />;
      case 'COMPLETED':
        return <CheckCircle />;
      case 'CANCELLED':
        return <Stop />;
      default:
        return <Assignment />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateProgress = () => {
    if (!campaign) return 0;
    const now = new Date();
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    
    if (now < start) return 0;
    if (now > end) return 100;
    
    const total = end.getTime() - start.getTime();
    const current = now.getTime() - start.getTime();
    return Math.round((current / total) * 100);
  };

  const getDaysRemaining = () => {
    if (!campaign) return 0;
    const now = new Date();
    const end = new Date(campaign.endDate);
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const handleStatusAction = async (newStatus: string) => {
    if (!campaign || !id) return;
    
    try {
      setActionLoading(true);
      
      // Determinar mensagem de sucesso baseada na ação
      let successMessage = '';
      switch (newStatus) {
        case 'ACTIVE':
          successMessage = 'Campanha ativada com sucesso! ✅';
          break;
        case 'DRAFT':
          successMessage = 'Campanha pausada com sucesso! ⏸️';
          break;
        default:
          successMessage = 'Status da campanha atualizado com sucesso!';
      }
      
      // Atualizar no backend
      await campaignService.updateCampaign(id, { status: newStatus as any });
      
      // Recarregar dados da campanha para garantir sincronização
      await fetchCampaign();
      
      // Mostrar notificação de sucesso
      showSuccess(successMessage);
      
      console.log(`✅ Status da campanha ${id} atualizado para: ${newStatus}`);
    } catch (err: any) {
      console.error('❌ Erro ao atualizar status:', err);
      const errorMessage = err.response?.data?.message || 'Erro ao atualizar status da campanha. Tente novamente.';
      showError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <AppHeader 
          title="Carregando Campanha..."
          subtitle="Visualize e gerencie sua campanha"
          icon={<CampaignIcon />}
        />
        <LinearProgress />
        <Container maxWidth="xl" sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Typography variant="h6" color="text.secondary">
              🔄 Carregando dados da campanha...
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  if (error || !campaign) {
    return (
      <>
        <AppHeader 
          title="Erro na Campanha"
          subtitle="Visualize e gerencie sua campanha"
          icon={<CampaignIcon />}
        />
        <Container maxWidth="xl" sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error || 'Campanha não encontrada'}
          </Alert>
        </Container>
      </>
    );
  }

  const progress = calculateProgress();
  const daysRemaining = getDaysRemaining();

  return (
    <>
      <AppHeader 
        title={`${campaign.name} - Dashboard`}
        subtitle="Visualize e gerencie sua campanha"
        icon={<CampaignIcon />}
      />

      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
          minHeight: '100vh',
          py: 4
        }}
      >
        <Container maxWidth="xl">
          <Fade in timeout={800}>
            <Box>
              {/* Header da Campanha */}
              <Paper elevation={3} sx={{ borderRadius: 3, p: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: theme.palette.primary.main,
                          width: 56,
                          height: 56
                        }}
                      >
                        {getStatusIcon(campaign.status)}
                      </Avatar>
                      <Box>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                          {campaign.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={getStatusLabel(campaign.status)}
                            color={getStatusColor(campaign.status) as any}
                            size="medium"
                            icon={getStatusIcon(campaign.status)}
                          />
                          {campaign.status === 'ACTIVE' && (
                            <Chip
                              label={`${daysRemaining} dias restantes`}
                              color="warning"
                              size="small"
                              icon={<AccessTime />}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    
                    {campaign.description && (
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {campaign.description}
                      </Typography>
                    )}

                    {/* Imagem da Campanha */}
                    <Box sx={{ mt: 3, mb: 2 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight={600} gutterBottom>
                        🖼️ Imagem da Campanha
                      </Typography>
                      <CampaignImage
                        imageUrl={campaign.imageUrl}
                        alt={campaign.name}
                        width="100%"
                        height="auto"
                        objectFit="contain"
                        borderRadius={2}
                      />
                    </Box>

                    {/* Progresso da Campanha */}
                    {campaign.status === 'ACTIVE' && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            Progresso da Campanha
                          </Typography>
                          <Typography variant="body2" color="primary" fontWeight={600}>
                            {progress}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={progress} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                            }
                          }} 
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Ações Rápidas */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                      size="large"
                      sx={{ minWidth: 160 }}
                    >
                      Editar
                    </Button>
                    
                    <ButtonGroup variant="outlined" size="small">
                      {campaign.status === 'DRAFT' && (
                        <Tooltip title="Ativar campanha">
                          <Button
                            onClick={() => handleStatusAction('ACTIVE')}
                            disabled={actionLoading}
                            startIcon={<PlayArrow />}
                          >
                            Ativar
                          </Button>
                        </Tooltip>
                      )}
                      {campaign.status === 'ACTIVE' && (
                        <Tooltip title="Pausar campanha temporariamente. Para reativar, clique em 'Ativar'.">
                          <Button
                            onClick={() => handleStatusAction('DRAFT')}
                            disabled={actionLoading}
                            startIcon={<Pause />}
                          >
                            Pausar
                          </Button>
                        </Tooltip>
                      )}
                    </ButtonGroup>
                  </Box>
                </Box>
              </Paper>

              {/* Cards de Estatísticas */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                {/* Estatísticas Principais */}
                <Card sx={{ 
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', lg: '1 1 calc(25% - 18px)' },
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: 'white',
                  borderRadius: 3
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h3" fontWeight={700}>
                          {campaign.stores?.length || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                          Lojas Participantes
                        </Typography>
                      </Box>
                      <Store sx={{ fontSize: 48, opacity: 0.7 }} />
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ 
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', lg: '1 1 calc(25% - 18px)' },
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                  color: 'white',
                  borderRadius: 3
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h3" fontWeight={700}>
                          {campaign.prizes?.length || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                          Prêmios
                        </Typography>
                      </Box>
                      <EmojiEvents sx={{ fontSize: 48, opacity: 0.7 }} />
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ 
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', lg: '1 1 calc(25% - 18px)' },
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  color: 'white',
                  borderRadius: 3
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h3" fontWeight={700}>
                          {campaign.sponsors?.length || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                          Patrocinadores
                        </Typography>
                      </Box>
                      <Business sx={{ fontSize: 48, opacity: 0.7 }} />
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ 
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', lg: '1 1 calc(25% - 18px)' },
                  background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                  color: 'white',
                  borderRadius: 3
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h3" fontWeight={700}>
                          {campaign.couponBatches?.length || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                          Lotes de Cupons
                        </Typography>
                      </Box>
                      <CardGiftcard sx={{ fontSize: 48, opacity: 0.7 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* Seção Principal - Informações e Dados */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {/* Informações Detalhadas */}
                <Box sx={{ flex: 2, minWidth: '400px' }}>
                  <Card sx={{ borderRadius: 3, mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Insights color="primary" />
                        Informações da Campanha
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                              📅 Data de Início
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {formatDate(campaign.startDate)}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                              🏁 Data de Fim
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {formatDate(campaign.endDate)}
                            </Typography>
                          </Box>
                        </Box>

                        {campaign.regulation && (
                          <Box>
                            <Typography variant="subtitle2" color="primary" fontWeight={600} gutterBottom>
                              📋 Regulamento
                            </Typography>
                            <Paper 
                              sx={{ 
                                p: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                borderRadius: 2
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                {campaign.regulation}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                {/* Sidebar - Listas e Detalhes */}
                <Box sx={{ flex: 1, minWidth: '350px' }}>
                  {/* Prêmios */}
                  {campaign.prizes && campaign.prizes.length > 0 && (
                    <Card sx={{ borderRadius: 3, mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmojiEvents color="warning" />
                          Prêmios ({campaign.prizes.length})
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List dense>
                          {campaign.prizes.map((prize, index) => (
                            <ListItem key={prize.id || index} sx={{ px: 0 }}>
                              <ListItemText
                                primary={
                                  <Typography variant="body1" fontWeight={600}>
                                    🏆 {prize.name} - Quantidade: {prize.quantity}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  )}

                  {/* Lojas Participantes */}
                  {campaign.stores && campaign.stores.length > 0 && (
                    <Card sx={{ borderRadius: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Store color="primary" />
                          Lojas Participantes ({campaign.stores.length})
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List dense>
                          {campaign.stores.map((store) => (
                            <ListItem key={store.id} sx={{ px: 0 }}>
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    🏪 {store.name}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2" color="text.secondary">
                                    📍 {store.address}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </>
  );
}; 
export default CampaignDetail;